import { ProgressBarState, toast } from '@aus-platform/design-system';
import axios from 'axios';
import { isNil } from 'lodash';
import { useRef, useState } from 'react';
import type {
  ApiResponse,
  FileDataType,
  FileInfoResponses,
  PresignedUrlResponse,
  StartMultipartUploadResponse,
} from '../../api';
import { fileInfoMapper, queryClient } from '../../api';
import api from '../../api/api';
import { UploadFilesErrorMessages } from '../../enums';
import { isFileSafe } from '../../helpers';
import { MultipartErrorMessage, MultipartErrorType } from './enums';
import {
  calculateChunkConfig,
  extractFileIdFromUrl,
  removeUploadIdFromStorage,
  storeUploadIdInStorage,
} from './helpers';
import type {
  AbortErrorReason,
  ChunkMap,
  MultipartUploadError,
  Part,
  Parts,
  UseMultipartUploadProps,
  UseMultipartUploadReturn,
} from './types';

const DEFAULT_TOTAL_PARTS = 100;

export const useMultipartUpload = ({
  fileType,
  urls: uploadURLs,
  allowedBinaryExtensions = [],
}: UseMultipartUploadProps): UseMultipartUploadReturn => {
  // States.
  const [progress, setProgress] = useState(ProgressBarState.Initial);
  // TODO: Remove after DSM as layer as there will fixed urls for multipart upload.
  const [urls, setUrls] = useState(uploadURLs);
  const [error, setError] = useState<string>();
  const [fileResponse, setFileResponse] = useState<FileDataType>();

  // Refs.
  const completedChunks = useRef<number>(0);
  const isPausedRef = useRef<boolean>(false);
  const uploadId = useRef<string>();
  const partPromises = useRef<Promise<Part>[]>([]);
  const pendingChunks = useRef<ChunkMap>(new Map());
  const currentProgress = useRef<number>(0);
  const maxPartCount = useRef<number>(DEFAULT_TOTAL_PARTS);
  const controllerRef = useRef(new AbortController());

  // Event Listener.
  controllerRef.current.signal.onabort = () => {
    if (isNil(uploadId.current)) {
      return;
    }

    // On pausing, instead of aborting entire upload process, setting isPausedRef back to false.
    isPausedRef.current = false;
  };

  // Helpers.
  const handleAbortError = (showToast) => {
    const signal = controllerRef.current.signal;
    const reason: AbortErrorReason = signal.reason;

    if (signal.aborted) {
      setError(reason.message);
      if (!showToast) {
        toast.error(reason.message);
      }
    }

    if (reason.type === MultipartErrorType.CancelledByUser) {
      setProgress(ProgressBarState.Cancelled);
    } else {
      setProgress(ProgressBarState.Error);
    }
  };

  const handleError = (error?: MultipartUploadError) => {
    const message = !axios.isAxiosError(error)
      ? error?.meta?.message
      : error?.message;

    console.error('API error:', message);
    toast.error('File upload failed. Try again');
    setError(message);
    setProgress(ProgressBarState.Error);
  };

  const initiateMultipartUpload = async (
    filename: string,
  ): Promise<string | undefined> => {
    if (!urls) {
      return;
    }

    try {
      if (controllerRef.current.signal.aborted) {
        controllerRef.current = new AbortController();
      }
      const response = await queryClient.fetchQuery({
        queryKey: [
          'multipart-upload/start-upload',
          filename,
          fileType,
          urls.startUploadUrl,
          controllerRef.current.signal,
        ],
        queryFn: () =>
          api.post<any, ApiResponse<StartMultipartUploadResponse>>(
            urls.startUploadUrl,
            {
              filename,
              filetype: fileType,
            },
            { signal: controllerRef.current.signal },
          ),
      });

      return response.data.upload_id;
    } catch (exception) {
      handleError(exception as MultipartUploadError);
    }
  };

  const getPresignedUrl = async (
    uploadId: string,
    partNumber: number,
  ): Promise<string | undefined> => {
    if (!urls) {
      return;
    }
    try {
      const response = await queryClient.fetchQuery({
        queryKey: [
          'multipart-upload/presigned-url',
          partNumber,
          uploadId,
          fileType,
          urls.presignedUrl,
          controllerRef.current.signal,
        ],
        queryFn: () =>
          api.post<any, ApiResponse<PresignedUrlResponse>>(
            urls.presignedUrl,
            {
              part_number: partNumber,
              upload_id: uploadId,
              filetype: fileType,
            },
            { signal: controllerRef.current.signal },
          ),
      });
      return response.data.url;
    } catch {
      if (!controllerRef.current.signal.aborted) {
        const abortReason: AbortErrorReason = {
          message: MultipartErrorMessage.FileUploadError,
          type: MultipartErrorType.FileUploadError,
        };

        controllerRef.current.abort(abortReason);
      }
    }
  };

  const uploadChunk = async (
    preSignedUrl: string,
    fileBlob: Blob,
    partNumber: number,
  ): Promise<Part> => {
    try {
      const response = await axios.put(preSignedUrl, fileBlob, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: controllerRef.current.signal,
        onUploadProgress: (event) => {
          if (event.total && !controllerRef.current.signal.aborted) {
            if (isPausedRef.current) {
              controllerRef.current.abort();
              controllerRef.current = new AbortController();
            }
          }
        },
      });

      completedChunks.current++;

      // Update progress after chunk completion.
      const progressPercentage = Math.round(
        (completedChunks.current / maxPartCount.current) * 100,
      );
      currentProgress.current = progressPercentage;
      setProgress(progressPercentage);

      const newPartPromise = Promise.resolve({
        ETag: response.headers['etag'],
        PartNumber: partNumber,
      });

      partPromises.current = [...partPromises.current, newPartPromise];

      if (completedChunks.current === maxPartCount.current) {
        finalizeUpload();
      }

      return {
        ETag: response.headers['etag'],
        PartNumber: partNumber,
      };
    } catch {
      // TODO: try re-uploading the chunk if it failed due to network error.
      return Promise.reject('File upload failed.');
    }
  };

  const completeMultipartUpload = async (
    uploadId: string,
    multiPartsArray: Parts,
  ) => {
    if (!urls) {
      return;
    }

    try {
      const response = await queryClient.fetchQuery({
        queryKey: [
          'multipart-upload/complete-upload',
          uploadId,
          fileType,
          multiPartsArray,
          urls.completeUploadUrl,
        ],
        queryFn: () =>
          api.post<any, ApiResponse<FileInfoResponses>>(
            urls.completeUploadUrl,
            {
              parts: multiPartsArray,
              upload_id: uploadId,
              filetype: fileType,
            },
          ),
      });

      // @FIXME: Not receiving file_info in site or iteration
      if (response.data.file_info) {
        setFileResponse(fileInfoMapper(response));
      }

      if (!controllerRef.current.signal.aborted) {
        setProgress(ProgressBarState.Completed);
      }
    } catch (exception) {
      if (!controllerRef.current.signal.aborted) {
        handleError(exception as MultipartUploadError);
      }
    } finally {
      // Remove uploadId from localStorage
      const fileId = extractFileIdFromUrl(urls?.completeUploadUrl || '');
      removeUploadIdFromStorage({
        uploadId: uploadId,
        fileId: fileId,
      });
    }
  };

  const pauseUpload = () => {
    isPausedRef.current = true;
    setProgress(currentProgress.current);
  };

  const processUpload = async () => {
    const storedBlobList = pendingChunks.current;
    const remainingBlobs = new Map(storedBlobList);

    // If resuming, start from the last known progress.
    if (currentProgress.current > 0) {
      setProgress(currentProgress.current);
    }

    if (isNil(uploadId.current)) {
      return;
    }

    // Store uploadId in localStorage
    const fileId = extractFileIdFromUrl(urls?.completeUploadUrl || '');
    storeUploadIdInStorage({
      uploadId: uploadId.current,
      fileId: fileId,
    });

    if (controllerRef.current.signal.aborted) {
      controllerRef.current = new AbortController();
    }
    isPausedRef.current = false;

    // Limit concurrent uploads.
    const MAX_CONCURRENT_UPLOADS = 6; // Browser limit is 6.
    let activeUploads = 0;
    const partNumbers = Array.from(storedBlobList.keys()).sort((a, b) => a - b);

    const uploadNextChunks = async () => {
      while (
        partNumbers.length > 0 &&
        activeUploads < MAX_CONCURRENT_UPLOADS &&
        !isPausedRef.current &&
        !controllerRef.current.signal.aborted
      ) {
        const partNumber = partNumbers.shift();
        if (!partNumber) {
          break;
        }

        const chunk = storedBlobList.get(partNumber);
        if (!chunk) {
          continue;
        }

        activeUploads++;

        try {
          const preSignedUrl = await getPresignedUrl(
            uploadId.current!,
            partNumber,
          );
          if (!preSignedUrl || controllerRef.current.signal.aborted) {
            activeUploads--;
            break;
          }

          uploadChunk(preSignedUrl, chunk, partNumber)
            .then(() => {
              // Remove the uploaded part from the local copy.
              remainingBlobs.delete(partNumber);
              pendingChunks.current = new Map(remainingBlobs);
            })
            .catch(() => {
              const abortReason: AbortErrorReason = {
                message: MultipartErrorMessage.FileUploadError,
                type: MultipartErrorType.FileUploadError,
              };
              controllerRef.current.abort(abortReason);
            })
            .finally(() => {
              activeUploads--;
              // Continue processing more chunks.
              uploadNextChunks();
            });
        } catch {
          activeUploads--;
          const abortReason: AbortErrorReason = {
            message: MultipartErrorMessage.FileUploadError,
            type: MultipartErrorType.FileUploadError,
          };
          controllerRef.current.abort(abortReason);
        }
      }
    };

    // Start the initial batch of uploads
    await uploadNextChunks();
  };

  // Managers.
  const startFileUpload = async (file: File) => {
    const isFileAllowed = await isFileSafe(file, false, {
      allowedBinaryExtensions,
    });

    if (!isFileAllowed) {
      setError(UploadFilesErrorMessages.BlacklistedFileFormat);
      setProgress(ProgressBarState.Error);
      return;
    }

    const { chunkSize, partsCount } = calculateChunkConfig(file.size);
    maxPartCount.current = partsCount;

    if (!urls) {
      throw new Error('URLs must be set before calling startUploading');
    }

    setProgress(ProgressBarState.Started);

    uploadId.current = await initiateMultipartUpload(file.name);

    const chunksMap = new Map<number, Blob>();

    for (let partNumber = 1; partNumber <= maxPartCount.current; partNumber++) {
      const chunkStart = (partNumber - 1) * chunkSize;
      const chunkEnd = partNumber * chunkSize;
      const chunk =
        partNumber < maxPartCount.current
          ? file.slice(chunkStart, chunkEnd)
          : file.slice(chunkStart);

      chunksMap.set(partNumber, chunk);
    }
    pendingChunks.current = new Map(chunksMap);

    processUpload();
  };

  const finalizeUpload = () => {
    Promise.all(partPromises.current)
      .then((multiPartsArray) => {
        setProgress(ProgressBarState.Processing);
        multiPartsArray.sort((a, b) => a.PartNumber - b.PartNumber);

        if (uploadId.current) {
          completeMultipartUpload(uploadId.current, multiPartsArray);
        }
        partPromises.current = [];
      })
      .catch(() => {
        if (!controllerRef.current.signal.aborted) {
          handleError();
        }
      });
  };

  const cancelUpload = async (
    showToast: boolean = false,
    id = uploadId.current,
  ) => {
    const abortReason: AbortErrorReason = {
      message: MultipartErrorMessage.CancelledByUser,
      type: MultipartErrorType.CancelledByUser,
    };
    controllerRef.current.abort(abortReason);
    // we call the completeMultipartUpload with wrong data to cancel the upload
    const dummyData = [
      {
        ETag: '1',
        PartNumber: 1,
      },
    ];
    if (id) {
      await completeMultipartUpload(id, dummyData);
    }
    handleAbortError(showToast);
  };

  const reset = (resetUrls: boolean = true) => {
    if (resetUrls) {
      setUrls(undefined);
    }
    setError(undefined);
    setProgress(ProgressBarState.Initial);
    controllerRef.current = new AbortController();
    currentProgress.current = 0;
    completedChunks.current = 0;
    partPromises.current = [];
  };

  return {
    progress,
    error,
    reset,
    setUrls,
    startUploading: startFileUpload,
    cancelUpload,
    fileResponse,
    resumeUpload: processUpload,
    pauseUpload,
  };
};
